import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { UniversalConfig } from '@application/universal_config.service';
import type {
	InformationGatheringPatientIdentity,
	InformationGatheringRole,
} from '@feature/information-gathering/information-gathering.model';
import { getCompletedInformationGathering } from '@feature/information-gathering/information-gathering-navigation';
import { useRespondentRepresentation } from '@feature/representation/use-respondent-representation';
import { useResolve } from '@visiba/instantiation';
import { NiN, NiNFormatter } from '@visiba/national-identity-number';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { CreateNewMessageAnalyticActions, useCreateNewMessage } from '../../hooks/use-create-new-message';
import type { CreateNewMessageSettings } from '../../hooks/use-create-new-message-settings';
import { useMessagingInformationGathering } from '../../hooks/use-messaging-information-gathering';

export type RespondentType = 'me' | 'representative' | string;

interface PresentationModel {
	respondentType: Nullable<RespondentType>;
	representablePatientId: Nullable<string>;
	representativeNin: Nullable<string>;
	representativeNinPlaceholder: string;
	currentCountry: keyof NiN.Implementations;
}

interface FormData {
	respondentType: RespondentType;
	representativeNin: Nullable<string>;
	representablePatientId: Nullable<string>;
	asAgentFor: Nullable<string>;
}

function createPresentationModel(settings: CreateNewMessageSettings, currentCountry: keyof NiN.Implementations): PresentationModel {
	const hasSelectedRepresentablePatient =
		settings.respondentType != null && !['me', 'representative'].includes(settings.respondentType);
	const respondentType =
		settings.representativeNin || hasSelectedRepresentablePatient ? 'representative' : settings.respondentType || 'me';

	return {
		respondentType,
		representablePatientId: hasSelectedRepresentablePatient ? settings.respondentType : null,
		representativeNin: settings.representativeNin,
		representativeNinPlaceholder: NiNFormatter.formatWithPlaceholder(currentCountry, '')[1] ?? '',
		currentCountry,
	};
}

export function useScreenWhoIsRespondent() {
	const createNewMessageController = useCreateNewMessage();
	const analyticService = useResolve(AnalyticService);
	const universalConfig = useResolve(UniversalConfig);
	const location = useLocation();
	const informationGatheringSubmissionId = getCompletedInformationGathering(location.state)?.submissionId;
	const informationGathering = useMessagingInformationGathering();
	const {
		featureUseRepresentatives,
		currentlyRepresentedPatient,
		representablePatients,
		isLoadingRepresentablePatients: isLoadingRepresentation,
	} = useRespondentRepresentation();
	const hasAutoNavigatedRepresentationRef = useRef(false);
	const settings =
		createNewMessageController.flowSettings.flowState === 'accessible' ? createNewMessageController.flowSettings.value : null;

	const allowRepresentative =
		createNewMessageController.flowData.flowState === 'accessible'
			? createNewMessageController.flowData.value.allowRepresentative
			: true;

	const getCurrentCountry = useCallback((): keyof NiN.Implementations => {
		const selectedCountryCode = universalConfig.selectedCountryCode.peek();

		return NiN.supportsCountryCode(selectedCountryCode) ? selectedCountryCode : 'SE';
	}, [universalConfig]);

	const data = useMemo<PresentationModel>(() => {
		if (settings == null) {
			return {
				respondentType: null,
				representablePatientId: null,
				representativeNin: null,
				representativeNinPlaceholder: NiNFormatter.formatWithPlaceholder(getCurrentCountry(), '')[1] ?? '',
				currentCountry: getCurrentCountry(),
			};
		}

		return createPresentationModel(settings, getCurrentCountry());
	}, [getCurrentCountry, settings]);

	const continueAfterRespondent = useCallback(
		(role: InformationGatheringRole, patientIdentity: InformationGatheringPatientIdentity, replace = false) => {
			void informationGathering.continueAfterRespondent(role, patientIdentity, { replace });
		},
		[informationGathering],
	);

	useEffect(() => {
		analyticService.trackPageView({
			customTitle: 'Create new message: Who is respondent',
		});
	}, [analyticService]);

	useEffect(() => {
		if (settings == null) {
			return;
		}

		if (informationGatheringSubmissionId) {
			createNewMessageController.updateFlowSettings({
				respondentType: 'me',
				representativeNin: null,
				asAgentFor: null,
				access: [],
			});
			createNewMessageController.goToScreen('reason-for-visit', { replace: true });
			return;
		}

		if (!featureUseRepresentatives) {
			analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.DoesNotUseRepresentatives);
			createNewMessageController.updateFlowSettings({
				respondentType: 'me',
				representativeNin: null,
				asAgentFor: null,
				access: [],
			});

			continueAfterRespondent('self', { dateOfBirth: '', nhsNumber: '' }, true);

			return;
		}

		// Deep-linked representative (RedRobin or `nin` query param) is only honoured when
		// "Patient med ombud" is active. When it is inactive the patient may only represent a
		// verified person (e.g. a child), so ignore the deep link and fall through.
		if (allowRepresentative && settings.asAgentFor && !settings.respondentType) {
			analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.AutoSetRespondentTypeRepresentative,
			);
			createNewMessageController.updateFlowSettings({
				respondentType: 'representative',
				representativeNin: settings.asAgentFor,
			});

			continueAfterRespondent('representative', { dateOfBirth: '', nhsNumber: settings.asAgentFor }, true);

			return;
		}

		if (isLoadingRepresentation) {
			return;
		}

		// User came back from the IdP-based representation switch (e.g. parent selected
		// a child to represent). Persist the representation in the basket settings and
		// move on to the next step so the screen does not get stuck rendering null.
		if (currentlyRepresentedPatient && !hasAutoNavigatedRepresentationRef.current) {
			hasAutoNavigatedRepresentationRef.current = true;

			analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.AutoSetRespondentTypeRepresentative,
			);
			createNewMessageController.updateFlowSettings({
				respondentType: 'representative',
				representativeNin: currentlyRepresentedPatient.IdentityNumber ?? null,
				asAgentFor: null,
				access: [],
			});

			continueAfterRespondent(
				'representative',
				{
					dateOfBirth: currentlyRepresentedPatient.DateOfBirth ?? '',
					nhsNumber: currentlyRepresentedPatient.IdentityNumber,
				},
				true,
			);

			return;
		}

		if (settings.redRobinSessionId && !settings.asAgentFor) {
			analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.AutoSetRespondentTypeMe);
			createNewMessageController.updateFlowSettings({
				respondentType: 'me',
				representativeNin: null,
				asAgentFor: null,
				access: [],
			});

			continueAfterRespondent('self', { dateOfBirth: '', nhsNumber: '' }, true);

			return;
		}

		// "Patient med ombud" (arbitrary representative) is inactive. If the patient also has no
		// representable persons (e.g. children) to select, there is nobody they can represent, so
		// skip the step and continue as themselves.
		if (!allowRepresentative && representablePatients.length === 0) {
			analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.AutoSetRespondentTypeMe);
			createNewMessageController.updateFlowSettings({
				respondentType: 'me',
				representativeNin: null,
				asAgentFor: null,
				access: [],
			});

			continueAfterRespondent('self', { dateOfBirth: '', nhsNumber: '' }, true);

			return;
		}
	}, [
		analyticService,
		createNewMessageController,
		currentlyRepresentedPatient,
		featureUseRepresentatives,
		isLoadingRepresentation,
		informationGatheringSubmissionId,
		allowRepresentative,
		representablePatients,
		settings,
		continueAfterRespondent,
	]);

	const submit = useCallback(
		(data: FormData) => {
			const currentCountry = getCurrentCountry();
			const representativeNin =
				data.respondentType === 'representative'
					? NiNFormatter.format(currentCountry, NiN.normalize(currentCountry, (data.representativeNin ?? '').trim()))
					: null;
			const asAgentFor = data.respondentType === 'representative' ? (data.asAgentFor ?? representativeNin) : null;
			createNewMessageController.updateFlowSettings({
				respondentType: data.respondentType,
				representativeNin,
				asAgentFor,
				access: [],
			});
			continueAfterRespondent(
				data.respondentType === 'me' ? 'self' : 'representative',
				data.respondentType === 'me' ? { dateOfBirth: '', nhsNumber: '' } : { dateOfBirth: '', nhsNumber: asAgentFor ?? '' },
			);
		},
		[continueAfterRespondent, createNewMessageController, getCurrentCountry],
	);

	return { data, submit, allowRepresentative };
}
